import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { B2BClient, B2BClientsFilters } from '../../modules/b2b/clients/types';
import { B2BPaginationMeta } from '../../modules/b2b/orders/types';

export interface B2BClientsState {
  clients: B2BClient[];
  activeClientId: string | null;
  activeClient: B2BClient | null;
  filters: B2BClientsFilters;
  meta: B2BPaginationMeta;
  isLoading: boolean;
  isLoadingActiveClient: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: B2BClientsState = {
  clients: [],
  activeClientId: null,
  activeClient: null,
  filters: {
    query: '',
  },
  meta: { page: 1, perPage: 20, total: 0 },
  isLoading: false,
  isLoadingActiveClient: false,
  isSaving: false,
  error: null,
};

export const name = 'b2bClients';

const b2bClientsSlice = createSlice({
  name,
  initialState,
  reducers: {
    setClients(state, { payload }: PayloadAction<B2BClient[]>) {
      state.clients = payload;
    },
    appendClients(state, { payload }: PayloadAction<B2BClient[]>) {
      payload.forEach((client) => {
        if (!state.clients.some(({ id }) => id === client.id)) {
          state.clients.push(client);
        }
      });
    },
    replaceClient(state, { payload }: PayloadAction<B2BClient>) {
      const index = state.clients.findIndex(({ id }) => id === payload.id);

      if (index !== -1) state.clients[index] = payload;
      if (state.activeClientId === payload.id) state.activeClient = payload;
    },
    setActiveClientId(state, { payload }: PayloadAction<string | null>) {
      state.activeClientId = payload;
    },
    setActiveClient(state, { payload }: PayloadAction<B2BClient | null>) {
      state.activeClient = payload;
    },
    clearActiveClient(state) {
      state.activeClientId = null;
      state.activeClient = null;
      state.isLoadingActiveClient = false;
    },
    setFilters(state, { payload }: PayloadAction<Partial<B2BClientsFilters>>) {
      state.filters = { ...state.filters, ...payload };
    },
    setMeta(state, { payload }: PayloadAction<B2BPaginationMeta>) {
      state.meta = payload;
    },
    setError(state, { payload }: PayloadAction<string | null>) {
      state.error = payload;
    },
    setIsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },
    setIsLoadingActiveClient(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingActiveClient = payload;
    },
    setIsSaving(state, { payload }: PayloadAction<boolean>) {
      state.isSaving = payload;
    },
  },
});

export const {
  appendClients,
  clearActiveClient,
  replaceClient,
  setActiveClient,
  setActiveClientId,
  setClients,
  setFilters,
  setMeta,
  setError,
  setIsLoading,
  setIsLoadingActiveClient,
  setIsSaving,
} = b2bClientsSlice.actions;

export default b2bClientsSlice.reducer;
